import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { LayoutGroup, motion } from 'framer-motion';
import React, { forwardRef, useId } from 'react';

import { ExtractHeadlessButtonProps } from '@/common/buttons';
import { ExtractLinkProps, isLinkProps } from '@/common/links';

import { TouchTarget } from './button';
import { Link } from './link';

export function Navbar({ className, ...props }: React.ComponentPropsWithoutRef<'nav'>) {
  return <nav {...props} className={clsx(className, 'flex flex-1 items-center gap-4 py-2.5 h-18 lg:mx-auto lg:max-w-7xl')} />;
}

export function NavbarDivider({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'h-6 w-px bg-zinc-950/10 dark:bg-white/10')} />;
}

export function NavbarSection({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, 'flex items-center gap-3')} />
    </LayoutGroup>
  );
}

export function NavbarSpacer({ className, ...props }: React.ComponentPropsWithoutRef<'div'>) {
  return <div aria-hidden="true" {...props} className={clsx(className, '-ml-4 flex-1')} />;
}

type NavbarItemProps = {
  current?: boolean;
  className?: string;
  children: React.ReactNode;
  /**
   * Primary indicates a navbar that uses the primary color, such as the top nav
   * bar. Neutral refers to a navbar that uses a neutral color, such as the
   * mobile drawer.
   */
  variant?: 'neutral' | 'primary';
} & (
  | ExtractHeadlessButtonProps
  | ExtractLinkProps
);

export const NavbarItem = forwardRef(function NavbarItem(
  {
    current,
    className,
    children,
    variant = 'primary',
    ...rest
  }: NavbarItemProps,
  ref: React.ForwardedRef<HTMLAnchorElement | HTMLButtonElement>
) {
  const classes = clsx(
    // Base
    'relative flex min-w-0 items-center gap-3 rounded-lg p-2 text-left text-base/6 font-medium text-white sm:text-sm/5 cursor-pointer',
    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6 *:data-[slot=icon]:shrink-0',
    // Trailing icon (down chevron or similar)
    '*:not-nth-2:last:data-[slot=icon]:ml-auto *:not-nth-2:last:data-[slot=icon]:size-5 sm:*:not-nth-2:last:data-[slot=icon]:size-4',
    // Avatar
    '*:data-[slot=avatar]:-m-0.5 *:data-[slot=avatar]:size-7 *:data-[slot=avatar]:[--avatar-radius:var(--radius-md)] *:data-[slot=avatar]:[--ring-opacity:10%] sm:*:data-[slot=avatar]:size-6',
    // Hover
    'data-hover:bg-zinc-950/5',
    // Active
    'data-active:bg-zinc-950/5',
    // Dark mode
    'dark:text-white',
    'dark:data-hover:bg-white/5',
    'dark:data-active:bg-white/5',
    // Colors
    variant === 'primary' && '*:data-[slot=icon]:fill-white data-hover:*:data-[slot=icon]:fill-white data-active:*:data-[slot=icon]:fill-white dark:*:data-[slot=icon]:fill-zinc-400 dark:data-hover:*:data-[slot=icon]:fill-white dark:data-active:*:data-[slot=icon]:fill-white',
    variant === 'neutral' && '*:data-[slot=icon]:fill-zinc-400 data-hover:*:data-[slot=icon]:fill-zinc-400 data-active:*:data-[slot=icon]:fill-zinc-400 dark:*:data-[slot=icon]:fill-white dark:data-hover:*:data-[slot=icon]:fill-zinc-400 dark:data-active:*:data-[slot=icon]:fill-zinc-400'
  );

  const props = rest as ExtractLinkProps | ExtractHeadlessButtonProps;

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="bg-zinc-950/5"
        />
      )}
      {isLinkProps(props) ?
          (
            <Link
              {...props as any}
              className={classes}
              data-current={current ? 'true' : undefined}
              ref={ref as React.ForwardedRef<HTMLAnchorElement>}
            >
              <TouchTarget>{children}</TouchTarget>
            </Link>
          ) :
          (
            <Headless.Button
              {...props}
              className={clsx('cursor-default', classes)}
              data-current={current ? 'true' : undefined}
              ref={ref}
            >
              <TouchTarget>{children}</TouchTarget>
            </Headless.Button>
          )}
    </span>
  );
});

export function NavbarLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'truncate')} />;
}
